import type { User } from "firebase/auth";
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type Timestamp,
  type Unsubscribe
} from "firebase/firestore";
import type { Asset, AssetAuditRecord, AuditValuationMethod, AuditorAccessRelease } from "../types/imobi";
import { db } from "./firebase";

export type SaveAuditorReleaseInput = {
  auditorCompanyId: string;
  auditorCompanyName: string;
  clientCompanyId: string;
  clientCompanyName: string;
  status: AuditorAccessRelease["status"];
  permissions: AuditorAccessRelease["permissions"];
};

export type SaveAssetAuditInput = {
  companyId: string;
  assetId: string;
  auditorCompanyId: string;
  auditorCompanyName: string;
  auditorUserName: string;
  status: AssetAuditRecord["status"];
  valuationMethod: AuditValuationMethod;
  auditedValue?: number;
  conservationState?: Asset["conservationState"];
  physicalCondition?: string;
  divergenceNotes?: string;
  referenceTitle?: string;
  referenceUrl?: string;
  notes?: string;
};

function serializeTimestamp(value: unknown): string {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as Timestamp).toDate().toISOString();
  }

  return typeof value === "string" ? value : "";
}

function mapRelease(id: string, data: Record<string, unknown>): AuditorAccessRelease {
  const permissions = data.permissions && typeof data.permissions === "object"
    ? data.permissions as Partial<AuditorAccessRelease["permissions"]>
    : {};

  return {
    id,
    auditorCompanyId: String(data.auditorCompanyId ?? ""),
    auditorCompanyName: String(data.auditorCompanyName ?? ""),
    clientCompanyId: String(data.clientCompanyId ?? ""),
    clientCompanyName: String(data.clientCompanyName ?? ""),
    status: (data.status ?? "ativo") as AuditorAccessRelease["status"],
    permissions: {
      canAuditAssets: permissions.canAuditAssets !== false,
      canViewValues: permissions.canViewValues === true,
      canEditAssetData: permissions.canEditAssetData === true
    },
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

function mapAuditRecord(id: string, data: Record<string, unknown>): AssetAuditRecord {
  return {
    id,
    companyId: String(data.companyId ?? ""),
    assetId: String(data.assetId ?? ""),
    auditorCompanyId: String(data.auditorCompanyId ?? ""),
    auditorCompanyName: String(data.auditorCompanyName ?? ""),
    auditorUserId: String(data.auditorUserId ?? ""),
    auditorUserName: String(data.auditorUserName ?? ""),
    status: (data.status ?? "auditado") as AssetAuditRecord["status"],
    valuationMethod: (data.valuationMethod ?? "manual_auditor") as AuditValuationMethod,
    auditedValue: typeof data.auditedValue === "number" ? data.auditedValue : undefined,
    conservationState: data.conservationState as Asset["conservationState"],
    physicalCondition: data.physicalCondition ? String(data.physicalCondition) : undefined,
    divergenceNotes: data.divergenceNotes ? String(data.divergenceNotes) : undefined,
    referenceTitle: data.referenceTitle ? String(data.referenceTitle) : undefined,
    referenceUrl: data.referenceUrl ? String(data.referenceUrl) : undefined,
    notes: data.notes ? String(data.notes) : undefined,
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

export function subscribeAuditorReleases(onChange: (releases: AuditorAccessRelease[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "auditorAccess"), orderBy("auditorCompanyName")),
    (snapshot) => onChange(snapshot.docs.map((releaseDoc) => mapRelease(releaseDoc.id, releaseDoc.data()))),
    (error) => onError(error.message)
  );
}

export function subscribeAuditorUserAccess(userId: string, onChange: (companyIds: string[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !userId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "auditorUserAccess"), where("userId", "==", userId), where("status", "==", "ativo")),
    (snapshot) => onChange(snapshot.docs.map((accessDoc) => String(accessDoc.data().clientCompanyId)).filter(Boolean)),
    (error) => onError(error.message)
  );
}

export function subscribeAssetAuditHistory(companyId: string, assetId: string, onChange: (records: AssetAuditRecord[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyId || !assetId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "companies", companyId, "assets", assetId, "auditHistory"), orderBy("createdAt", "desc")),
    (snapshot) => onChange(snapshot.docs.map((historyDoc) => mapAuditRecord(historyDoc.id, historyDoc.data()))),
    (error) => onError(error.message)
  );
}

export async function saveAuditorRelease(input: SaveAuditorReleaseInput, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  if (input.auditorCompanyId === input.clientCompanyId) {
    throw new Error("Empresa auditora e empresa cliente precisam ser diferentes.");
  }

  const releaseId = `${input.auditorCompanyId}_${input.clientCompanyId}`;
  const firestore = db;
  const accessSnapshot = await getDocs(query(collection(firestore, "companyAccess"), where("companyId", "==", input.auditorCompanyId)));
  const auditorUserAccess = accessSnapshot.docs
    .map((accessDoc) => accessDoc.data())
    .filter((access) => access.status === "ativo" && access.companyRole === "AUDITOR")
    .map((access) => String(access.userId))
    .filter(Boolean);

  const batch = writeBatch(firestore);
  batch.set(doc(firestore, "auditorAccess", releaseId), {
    ...input,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    createdAt: serverTimestamp(),
    createdBy: user.uid
  }, { merge: true });

  auditorUserAccess.forEach((auditorUserId) => {
    batch.set(doc(firestore, "auditorUserAccess", `${auditorUserId}_${input.clientCompanyId}`), {
      userId: auditorUserId,
      auditorCompanyId: input.auditorCompanyId,
      auditorCompanyName: input.auditorCompanyName,
      clientCompanyId: input.clientCompanyId,
      clientCompanyName: input.clientCompanyName,
      releaseId,
      status: input.status,
      permissions: input.permissions,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
      createdAt: serverTimestamp(),
      createdBy: user.uid
    }, { merge: true });
  });

  await batch.commit();
}

export async function saveAssetAudit(input: SaveAssetAuditInput, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const auditId = crypto.randomUUID();
  const assetRef = doc(db, "companies", input.companyId, "assets", input.assetId);
  const historyRef = doc(db, "companies", input.companyId, "assets", input.assetId, "auditHistory", auditId);
  const nowIso = new Date().toISOString();

  const auditValuation = {
    method: input.valuationMethod,
    value: input.auditedValue ?? 0,
    referenceTitle: input.referenceTitle ?? "",
    referenceUrl: input.referenceUrl ?? "",
    referenceAttachmentUrls: [],
    referenceCapturedAt: nowIso,
    notes: input.notes ?? ""
  };

  await setDoc(historyRef, {
    ...input,
    auditorUserId: user.uid,
    createdAt: serverTimestamp(),
    createdBy: user.uid,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });

  await updateDoc(assetRef, {
    auditStatus: input.status,
    auditedValue: input.auditedValue ?? null,
    auditValuation,
    auditorCompanyId: input.auditorCompanyId,
    auditorCompanyName: input.auditorCompanyName,
    auditedAt: serverTimestamp(),
    auditedBy: user.uid,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}
